import { computed, inject, provide, ref, shallowRef, watch, type InjectionKey } from 'vue';
import {
  archiveReadiness,
  buildReportTable,
  compileMatrix,
  createEmptyAnswers,
  getFieldDefinition,
  parseExtraCellNodeId,
  selectionAfterRowShift,
  withExtraRowAppended,
  withExtraRowCellValue,
  withFieldValue,
  withNewExtraRowRemoved,
  type CompiledFieldNode,
  type CompiledForm,
  type ControlSpec,
  type EngineFieldType,
  type FieldValue,
  type FillSubject,
  type FormElement,
  type FormPermissionsStructure,
  type FormRulesStructure,
  type FormStructure,
  type MatrixAnswers,
  type SelectOption,
} from '@record-fill';
import {
  getElementRegistryEntry,
  matrixBuilderApi,
  permissionsStructureSchema,
  usePermissionSubjects,
  type Matrix,
} from '@/features/FormBuilder';
import { exportToExcel } from '@/shared/lib';
import { buildElementContract, countElementTypes, sampleElementOfEachType, walkElements } from './element-contract';
import { LAB_VIEW_MODE_STORAGE_KEY, parseViewMode, type LabViewMode } from './lab-view-mode';
import { buildExportSheet } from './report-export';
import { buildRuleTrace, resolveGrants } from './rule-trace';

export type LabRule = FormRulesStructure['rules'][number];
export type LabGrant = FormPermissionsStructure['fields'][number];

export interface FieldOption {
  id: string;
  label: string;
  type: EngineFieldType;
  kind: ControlSpec['kind'];
  typeLabel: string;
  options: SelectOption[];
  isTrigger: boolean;
}

export interface SavedResponse {
  id: number;
  label: string;
  subjectLabel: string;
  form: CompiledForm;
}

export interface NewRuleInput {
  triggerFieldId: string;
  triggerOption: string;
  action: LabRule['action'];
  targetFieldIds: string[];
}

const EMPTY_SUBJECT: FillSubject = { positionIds: [], functionalActivityIds: [] };

function thenBlocksTarget(rule: LabRule, fieldId: string): boolean {
  return rule.thenBlocks.some((block) => block.targets.some((target) => target.fieldIds.includes(fieldId)));
}

function readRules(raw: object | null | undefined): FormRulesStructure | null {
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as { rules?: unknown }).rules)) return null;
  return raw as FormRulesStructure;
}

function readPermissions(raw: object | null | undefined): FormPermissionsStructure | null {
  const parsed = permissionsStructureSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

function withRequired(element: FormElement, ids: Set<string>): FormElement {
  if (element.type === 'TABLE') {
    const cells = (element.properties as { cells?: Array<Array<{ content?: FormElement }>> }).cells ?? [];
    return {
      ...element,
      properties: {
        ...element.properties,
        cells: cells.map((row) =>
          row.map((cell) => (cell.content ? { ...cell, content: withRequired(cell.content, ids) } : cell)),
        ),
      } as FormElement['properties'],
    };
  }
  if (!ids.has(element.id)) return element;
  return { ...element, properties: { ...element.properties, required: true } };
}

interface CellRef {
  content?: FormElement;
  rowIndex: number;
  colIndex: number;
  isMerged?: boolean;
}

function cellText(cell: CellRef | undefined, allowCheckbox: boolean): string | null {
  const content = cell?.content;
  if (!content) return null;
  if (content.type === 'FIXED_TEXT') return content.properties.content?.trim() || null;
  if (allowCheckbox && content.type === 'CHECKBOX') {
    const options = (content.properties as { options?: Array<{ text: string }> }).options ?? [];
    return options.map((option) => option.text).join(' / ') || null;
  }
  return null;
}

function nearestText(grid: CellRef[][], rowIndex: number, colIndex: number, allowCheckbox: boolean): string | null {
  const row = grid[rowIndex];
  let hint: string | null = null;
  for (let c = colIndex - 1; c >= 0 && !hint; c -= 1) hint = cellText(row[c], allowCheckbox);
  for (let r = rowIndex - 1; r >= 0 && !hint; r -= 1) hint = cellText(grid[r]?.[colIndex], allowCheckbox);
  return hint;
}

// Fields inside a table rarely carry a label; the nearest static text on the same row, then
// the same column, is how a person would name them, so the lab uses it as a nickname. A
// checkbox's option text only names a neighbour when no static text is around at all.
function buildFieldNicknames(elements: FormElement[]): Map<string, string> {
  const nicknames = new Map<string, string>();
  for (const element of elements) {
    if (element.type !== 'TABLE') continue;
    const grid = ((element.properties as { cells?: CellRef[][] }).cells ?? []).map((row) =>
      row.filter((cell) => cell.isMerged !== true),
    );
    grid.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (!cell.content || !getFieldDefinition(cell.content)) return;
        const hint = nearestText(grid, rowIndex, colIndex, false) ?? nearestText(grid, rowIndex, colIndex, true);
        if (hint) nicknames.set(cell.content.id, hint);
      });
    });
  }
  return nicknames;
}

function readStoredViewMode(): LabViewMode {
  try {
    return parseViewMode(window.localStorage.getItem(LAB_VIEW_MODE_STORAGE_KEY));
  } catch {
    return 'developer';
  }
}

function storeViewMode(mode: LabViewMode): void {
  try {
    window.localStorage.setItem(LAB_VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Storage may be unavailable; the view still switches for the session.
  }
}

export function useMatrixFillLab() {
  const matrices = ref<Matrix[]>([]);
  const matrix = shallowRef<Matrix | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const subjects = usePermissionSubjects();
  const selectedPositionIds = ref<number[]>([]);
  const selectedActivityIds = ref<number[]>([]);
  const defaultAccess = ref<'open' | 'closed'>('open');

  const useStoredRules = ref(true);
  const labRules = ref<LabRule[]>([]);
  const useStoredPermissions = ref(true);
  const labGrants = ref<LabGrant[]>([]);
  const requiredFieldIds = ref<Set<string>>(new Set());

  const answers = ref<MatrixAnswers>(createEmptyAnswers(0));
  const selectedFieldId = ref<string | null>(null);
  const responses = ref<SavedResponse[]>([]);
  const reportFieldIds = ref<Set<string> | null>(null);
  const isExporting = ref(false);
  const archiveAttempted = ref(false);
  const viewMode = ref<LabViewMode>(readStoredViewMode());
  watch(viewMode, storeViewMode);

  const baseStructure = computed<FormStructure | null>(() =>
    matrix.value ? { elements: matrix.value.matrixStructure.elements, version: matrix.value.matrixStructure.version } : null,
  );

  const structure = computed<FormStructure | null>(() => {
    if (!baseStructure.value) return null;
    if (requiredFieldIds.value.size === 0) return baseStructure.value;
    return {
      ...baseStructure.value,
      elements: baseStructure.value.elements.map((element) => withRequired(element, requiredFieldIds.value)),
    };
  });

  const subject = computed<FillSubject>(() => ({
    positionIds: selectedPositionIds.value,
    functionalActivityIds: selectedActivityIds.value,
  }));

  const subjectLabel = computed(() => {
    const names = [
      ...subjects.positions.value.filter((p) => selectedPositionIds.value.includes(p.id)).map((p) => p.name),
      ...subjects.functionalActivities.value
        .filter((a) => selectedActivityIds.value.includes(a.id))
        .map((a) => a.name),
    ];
    return names.length > 0 ? names.join(', ') : 'Sem cargo ou atividade';
  });

  const baseline = computed<CompiledForm | null>(() =>
    baseStructure.value
      ? compileMatrix({
          structure: baseStructure.value,
          rules: null,
          permissions: null,
          subject: EMPTY_SUBJECT,
          answers: createEmptyAnswers(matrix.value?.id ?? 0),
        })
      : null,
  );

  const fieldOptions = computed<FieldOption[]>(() => {
    const form = baseline.value;
    if (!form || !baseStructure.value) return [];
    const nicknames = buildFieldNicknames(baseStructure.value.elements);
    const used = new Map<string, number>();
    return form.fieldOrder.map((id) => {
      const field = form.fields[id];
      const options = 'options' in field.control ? field.control.options : [];
      const typeLabel = getElementRegistryEntry(field.type)?.label ?? field.type;
      const ownLabel = field.element.properties.label?.trim();
      const optionLabel = field.control.kind === 'checkbox' ? options.map((option) => option.label).join(' / ') : '';
      let label = ownLabel || nicknames.get(id) || optionLabel || field.element.properties.name;
      const count = (used.get(label) ?? 0) + 1;
      used.set(label, count);
      if (count > 1) label = `${label} (${count})`;
      return { id, label, type: field.type, kind: field.control.kind, typeLabel, options, isTrigger: options.length > 0 };
    });
  });

  const storedRules = computed(() => readRules(matrix.value?.rulesStructure));
  const storedPermissions = computed(() => readPermissions(matrix.value?.permissionsStructure));
  const anchorFieldIds = computed(() => storedRules.value?.anchorFieldIds ?? []);
  const taggedReportFieldIds = computed(() => storedRules.value?.reportFieldIds ?? []);

  // Anchor and report tags come from the matrix even when the lab switches the stored
  // conditional rules off: they are configuration of the matrix, not of the scenario.
  const rules = computed<FormRulesStructure | null>(() => {
    const list = [...(useStoredRules.value ? storedRules.value?.rules ?? [] : []), ...labRules.value];
    if (list.length === 0 && anchorFieldIds.value.length === 0 && taggedReportFieldIds.value.length === 0) return null;
    return {
      version: 1,
      rules: list,
      nextRuleNumber: list.length + 1,
      anchorFieldIds: anchorFieldIds.value,
      reportFieldIds: taggedReportFieldIds.value,
    };
  });

  const permissions = computed<FormPermissionsStructure | null>(() => {
    const merged = new Map<string, LabGrant>();
    if (useStoredPermissions.value) for (const grant of storedPermissions.value?.fields ?? []) merged.set(grant.fieldId, grant);
    for (const grant of labGrants.value) merged.set(grant.fieldId, grant);
    return merged.size > 0 ? { version: 1, fields: [...merged.values()] } : null;
  });

  const compilation = computed<{ form: CompiledForm; ms: number } | null>(() => {
    if (!structure.value) return null;
    const start = performance.now();
    const form = compileMatrix({
      structure: structure.value,
      rules: rules.value,
      permissions: permissions.value,
      subject: subject.value,
      answers: answers.value,
      options: { defaultAccess: defaultAccess.value },
    });
    return { form, ms: performance.now() - start };
  });

  const compiled = computed<CompiledForm | null>(() => compilation.value?.form ?? null);
  const compileTimeMs = computed(() => compilation.value?.ms ?? 0);

  // A valid value for any field kind, so scenarios work on matrices without text fields.
  function sampleValue(fieldId: string, variant: number): FieldValue {
    const field = fieldOptions.value.find((option) => option.id === fieldId);
    if (!field) return null;
    switch (field.type) {
      case 'DATE_INPUT':
        return `2026-02-${String(variant).padStart(2, '0')}`;
      case 'EMAIL_INPUT':
        return `exemplo${variant}@htm.com.br`;
      case 'URL_INPUT':
        return `https://exemplo${variant}.htm.com.br`;
      case 'REGISTER_NUMBER_INPUT':
        return String(variant).padStart(6, '0');
      default:
        break;
    }
    switch (field.kind) {
      case 'number':
        return variant;
      case 'select':
      case 'radio':
        return field.options[(variant - 1) % Math.max(field.options.length, 1)]?.value ?? '';
      case 'checkbox':
        return field.options.length > 0 ? [field.options[(variant - 1) % field.options.length].value] : [];
      case 'text':
      default:
        return `Exemplo ${variant}`;
    }
  }

  function ruleFires(rule: LabRule): boolean {
    const field = compiled.value?.fields[rule.triggerFieldId];
    if (!field) return false;
    return getFieldDefinition(field.element)?.triggerMatches(field.element, field.value, rule.triggerOption) ?? false;
  }

  const inspected = computed<CompiledFieldNode | null>(() =>
    selectedFieldId.value ? compiled.value?.fields[selectedFieldId.value] ?? null : null,
  );

  const inspectedRules = computed(() =>
    (rules.value?.rules ?? []).filter(
      (rule) =>
        rule.triggerFieldId === selectedFieldId.value ||
        rule.targetFieldIds.includes(selectedFieldId.value ?? '') ||
        thenBlocksTarget(rule, selectedFieldId.value ?? ''),
    ),
  );

  const inspectedGrant = computed(
    () => permissions.value?.fields.find((grant) => grant.fieldId === selectedFieldId.value) ?? null,
  );

  const inspectedColumns = computed(() => {
    const field = inspected.value;
    if (!field) return [];
    return getFieldDefinition(field.element)?.columns(field.element, field.label) ?? [];
  });

  const archive = computed(() => (compiled.value ? archiveReadiness(compiled.value) : null));

  const anchorFields = computed(() => {
    const form = compiled.value;
    if (!form) return [];
    return form.fieldOrder
      .map((id) => form.fields[id])
      .filter((field) => field.tags.anchor)
      .map((field) => ({
        id: field.id,
        filled: !archive.value?.missing.includes(field.id),
        hiddenByRule: field.state.hiddenByRule,
      }));
  });

  // The engine defaults the report to the report-tagged fields; the lab mirrors that choice
  // so the checkbox list shows what the Excel will actually carry.
  const effectiveReportFieldIds = computed<Set<string>>(() => {
    if (reportFieldIds.value) return reportFieldIds.value;
    const form = compiled.value;
    if (!form) return new Set();
    const tagged = form.fieldOrder.filter((id) => form.fields[id].tags.report);
    return new Set(tagged.length > 0 ? tagged : form.fieldOrder);
  });

  const report = computed(() => {
    if (!compiled.value) return null;
    const table = buildReportTable(compiled.value, responses.value.map((response) => response.form), {
      fieldIds: reportFieldIds.value ? [...reportFieldIds.value] : undefined,
    });
    const labels = new Map(fieldOptions.value.map((field) => [field.id, field.label]));
    return {
      ...table,
      columns: table.columns.map((column) => {
        const engineLabel = compiled.value?.fields[column.fieldId]?.label ?? '';
        const labLabel = labels.get(column.fieldId);
        if (!labLabel || !engineLabel || !column.label.startsWith(engineLabel)) return column;
        return { ...column, label: labLabel + column.label.slice(engineLabel.length) };
      }),
    };
  });

  const stats = computed(() => {
    const form = compiled.value;
    if (!form) return null;
    const fields = form.fieldOrder.map((id) => form.fields[id]);
    return {
      total: fields.length,
      hidden: fields.filter((field) => !field.state.visible).length,
      disabled: fields.filter((field) => field.state.visible && !field.state.enabled).length,
      locked: fields.filter((field) => field.state.visible && !field.state.editable).length,
      unsupported: form.nodes.filter((node) => node.kind === 'unsupported').length,
      issues: form.issues.length,
    };
  });

  // Names for anything a rule can target: fields by nickname, fixed texts by their content.
  const elementLabels = computed(() => {
    const labels = new Map(fieldOptions.value.map((field) => [field.id, field.label]));
    for (const element of walkElements(baseStructure.value?.elements ?? [])) {
      if (labels.has(element.id)) continue;
      if (element.type === 'FIXED_TEXT') {
        labels.set(element.id, `"${(element.properties as { content?: string }).content?.trim() ?? ''}"`);
      } else if (element.type === 'TABLE') {
        labels.set(element.id, 'tabela');
      } else {
        labels.set(element.id, element.properties.name ?? element.id);
      }
    }
    return labels;
  });

  const elementContract = computed(() => buildElementContract(baseStructure.value?.elements ?? []));
  const elementSamples = computed(() => sampleElementOfEachType(baseStructure.value?.elements ?? []));

  const inputSummary = computed(() => {
    const counts = countElementTypes(baseStructure.value?.elements ?? []);
    return {
      elements: Object.values(counts).reduce((total, count) => total + count, 0),
      fields: compiled.value?.fieldOrder.length ?? 0,
      tables: counts.TABLE,
      rules: rules.value?.rules.length ?? 0,
      grants: permissions.value?.fields.length ?? 0,
      anchors: anchorFieldIds.value.length,
      reportFields: taggedReportFieldIds.value.length,
    };
  });

  const ruleTrace = computed(() =>
    compiled.value ? buildRuleTrace(rules.value?.rules ?? [], compiled.value, ruleFires) : [],
  );

  const grantResolutions = computed(() => resolveGrants(permissions.value?.fields ?? [], subject.value));

  async function loadList(): Promise<void> {
    try {
      matrices.value = (await matrixBuilderApi.listMatrices(1, 100)).matrices;
    } catch {
      error.value = 'Não foi possível listar as matrizes.';
    }
  }

  async function load(id: number): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      matrix.value = await matrixBuilderApi.loadMatrix(id);
      resetLab();
    } catch {
      matrix.value = null;
      error.value = 'Não foi possível carregar a matriz.';
    } finally {
      isLoading.value = false;
    }
  }

  function resetLab(): void {
    answers.value = createEmptyAnswers(matrix.value?.id ?? 0);
    labRules.value = [];
    labGrants.value = [];
    requiredFieldIds.value = new Set();
    responses.value = [];
    reportFieldIds.value = null;
    archiveAttempted.value = false;
    selectedFieldId.value = null;
    selectedPositionIds.value = [];
    selectedActivityIds.value = [];
    defaultAccess.value = 'open';
  }

  function setValue(fieldId: string, value: FieldValue): void {
    answers.value = withFieldValue(answers.value, fieldId, value);
  }

  function setCellValue(tableId: string, rowIndex: number, columnIndex: number, value: FieldValue): void {
    answers.value = withExtraRowCellValue(answers.value, tableId, rowIndex, columnIndex, value);
  }

  // The lab has no saved rows, so every row of a table is new and "+" drops any highlight on it.
  function addExtraRow(tableId: string): void {
    answers.value = withExtraRowAppended(answers.value, tableId);
    selectedFieldId.value = selectionAfterRowShift(selectedFieldId.value, tableId, 0);
  }

  function removeExtraRow(tableId: string, rowIndex: number): void {
    const current = compiled.value;
    if (!current) return;
    const next = withNewExtraRowRemoved(current, answers.value, tableId, rowIndex);
    if (next === answers.value) return;
    answers.value = next;
    selectedFieldId.value = selectionAfterRowShift(selectedFieldId.value, tableId, rowIndex);
  }

  function clearAnswers(): void {
    answers.value = createEmptyAnswers(matrix.value?.id ?? 0);
    archiveAttempted.value = false;
    if (selectedFieldId.value && parseExtraCellNodeId(selectedFieldId.value)) selectedFieldId.value = null;
  }

  function tryArchive(): void {
    archiveAttempted.value = true;
    const first = archive.value?.missing[0];
    if (first) locateField(first);
  }

  function locateField(fieldId: string): void {
    selectedFieldId.value = fieldId;
    document
      .querySelector(`[data-testid="fill-field-${fieldId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function addRule(input: NewRuleInput): void {
    const id = `lab-rule-${labRules.value.length + 1}-${Date.now()}`;
    labRules.value = [
      ...labRules.value,
      { ...input, id, name: `Regra ${labRules.value.length + 1}`, thenBlocks: [], createdAt: new Date().toISOString() },
    ];
  }

  function removeRule(id: string): void {
    labRules.value = labRules.value.filter((rule) => rule.id !== id);
  }

  function setGrant(fieldId: string, positionIds: number[], functionalActivityIds: number[]): void {
    labGrants.value = [
      ...labGrants.value.filter((grant) => grant.fieldId !== fieldId),
      { fieldId, positionIds, functionalActivityIds },
    ];
  }

  function removeGrant(fieldId: string): void {
    labGrants.value = labGrants.value.filter((grant) => grant.fieldId !== fieldId);
  }

  function toggleRequired(fieldId: string): void {
    const next = new Set(requiredFieldIds.value);
    if (next.has(fieldId)) next.delete(fieldId);
    else next.add(fieldId);
    requiredFieldIds.value = next;
  }

  function saveResponse(): void {
    if (!compiled.value || compiled.value.issues.length > 0) return;
    const id = responses.value.length + 1;
    responses.value = [
      ...responses.value,
      { id, label: `Resposta ${id}`, subjectLabel: subjectLabel.value, form: compiled.value },
    ];
    clearAnswers();
  }

  function removeResponse(id: number): void {
    responses.value = responses.value.filter((response) => response.id !== id);
  }

  function toggleReportField(fieldId: string): void {
    const next = new Set(effectiveReportFieldIds.value);
    if (next.has(fieldId)) next.delete(fieldId);
    else next.add(fieldId);
    reportFieldIds.value = next;
  }

  async function exportReport(): Promise<void> {
    const table = report.value;
    if (!table || table.rows.length === 0 || isExporting.value) return;
    isExporting.value = true;
    try {
      const sheet = buildExportSheet(table);
      await exportToExcel(
        sheet.rows,
        sheet.columns.map((column) => ({
          header: column.header,
          getValue: (row: Record<string, string>) => row[column.key] ?? '',
          width: 24,
        })),
        { sheetName: 'Respostas', fileName: `poc-matriz-${matrix.value?.id ?? 0}.xlsx` },
      );
    } finally {
      isExporting.value = false;
    }
  }

  return {
    matrices,
    matrix,
    isLoading,
    error,
    subjects,
    selectedPositionIds,
    selectedActivityIds,
    defaultAccess,
    subject,
    subjectLabel,
    useStoredRules,
    labRules,
    storedRules,
    useStoredPermissions,
    labGrants,
    storedPermissions,
    requiredFieldIds,
    answers,
    selectedFieldId,
    responses,
    reportFieldIds,
    effectiveReportFieldIds,
    anchorFieldIds,
    taggedReportFieldIds,
    anchorFields,
    archive,
    archiveAttempted,
    isExporting,
    fieldOptions,
    rules,
    permissions,
    compiled,
    compileTimeMs,
    viewMode,
    elementLabels,
    elementContract,
    elementSamples,
    inputSummary,
    ruleTrace,
    grantResolutions,
    inspected,
    inspectedRules,
    inspectedGrant,
    inspectedColumns,
    report,
    stats,
    ruleFires,
    sampleValue,
    loadList,
    load,
    resetLab,
    setValue,
    setCellValue,
    addExtraRow,
    removeExtraRow,
    clearAnswers,
    tryArchive,
    locateField,
    addRule,
    removeRule,
    setGrant,
    removeGrant,
    toggleRequired,
    saveResponse,
    removeResponse,
    toggleReportField,
    exportReport,
  };
}

export type MatrixFillLab = ReturnType<typeof useMatrixFillLab>;

const LAB_KEY: InjectionKey<MatrixFillLab> = Symbol('matrix-fill-lab');

export function provideMatrixFillLab(): MatrixFillLab {
  const lab = useMatrixFillLab();
  provide(LAB_KEY, lab);
  return lab;
}

export function useMatrixFillLabContext(): MatrixFillLab {
  const lab = inject(LAB_KEY);
  if (!lab) throw new Error('MatrixFillLab não foi provido');
  return lab;
}
